/**
 * "É um de" / "Não é um de" — a condição que escolhe VÁRIOS valores.
 *
 * ## Por que ela precisou existir
 *
 * O filtro salvo tem dois grupos: combinadas (E) e independentes (OU), e o
 * resultado é (todas as combinadas) E (pelo menos uma das independentes). Um
 * balde de OU só. Então "(status A ou B ou C) E (serviço X ou Y)" é
 * inexprimível — e quem precisa disso repete a condição no grupo E, que é a
 * única coisa que a tela deixava fazer.
 *
 * `status = Aberto E status = Pendente` não casa nada. Medido em produção em
 * 16/10/2026: QUATRO kanbans de equipe estavam assim, mostrando zero chamado
 * sem nenhum erro na tela.
 *
 * ## Por que a regra mora aqui, e não na tela
 *
 * Porque a condição é avaliada em DOIS lugares: o servidor aplica na consulta
 * (é ele que pagina) e a tela reavalia sobre as linhas já carregadas. Ensinar
 * só um dos lados dá o pior desfecho possível — o servidor devolve as linhas
 * certas e a tela descarta todas. Já aconteceu nesta base, e está documentado
 * dentro de `utils/filtroDinamico.js`.
 */

export const OPERADORES_DE_LISTA = ['in', 'not_in'];

export const ehOperadorDeLista = (operador) => OPERADORES_DE_LISTA.includes(operador);

/**
 * Os valores de uma condição de lista, venham como vierem.
 *
 * A condição mora numa coluna de TEXTO no banco: o que o modal guarda como
 * array volta como JSON em string. Lista separada por vírgula também existe
 * nesta base. Espelha `FilterService::valoresDaCondicao`, do outro lado.
 */
export const valoresDaCondicao = (valor) => {
    if (Array.isArray(valor)) return valor;

    if (typeof valor === 'string' && valor !== '') {
        try {
            const decodificado = JSON.parse(valor);
            if (Array.isArray(decodificado)) return decodificado;
        } catch (_) {
            // Não é JSON; cai na vírgula.
        }

        return valor.split(',').map((v) => v.trim()).filter((v) => v !== '');
    }

    if (valor !== null && valor !== undefined && valor !== '') return [valor];

    return [];
};

/**
 * O chamado casa a condição de lista?
 *
 * Devolve `null` quando o operador não é de lista -- aí quem chamou segue pelo
 * caminho normal.
 *
 * Comparado como TEXTO: o valor do chamado vem como número e a condição vem do
 * banco como string, e `2 === '2'` é falso.
 *
 * ## O nulo no "não é um de"
 *
 * Chamado SEM valor entra, e isso é deliberado: ele de fato não é nenhum dos
 * escolhidos. É o que o servidor faz (`orWhereNull`), e é o ponto em que os
 * dois lados mais fácil divergiriam -- a avaliação da tela descarta valor nulo
 * antes de olhar o operador.
 *
 * @param {*} valorDoChamado
 * @param {*} valorDaCondicao
 * @param {string} operador
 * @returns {boolean|null}
 */
export const avaliarOperadorDeLista = (valorDoChamado, valorDaCondicao, operador) => {
    if (!ehOperadorDeLista(operador)) return null;

    const valores = valoresDaCondicao(valorDaCondicao).map((v) => String(v));

    // Lista vazia: "é um de nenhum" não casa ninguém, e "não é nenhum de
    // nenhum" não exclui ninguém. Mesmo par do servidor.
    if (valores.length === 0) return operador === 'not_in';

    const vazio = valorDoChamado === null || valorDoChamado === undefined || valorDoChamado === '';

    if (vazio) return operador === 'not_in';

    const casa = valores.includes(String(valorDoChamado));

    return operador === 'in' ? casa : !casa;
};
